import { clsx } from 'clsx';
import React from 'react';
import { DrawerField, DrawerSectionHeader } from '../../../components/drawer-components';
import { TooltippedText } from '../../../components/tooltipped-text';
import { EMPTY_VALUE } from './providerDisplay';

/** Label/value grid used by every drawer tab (same shape as the clearance details tab). */
export const FIELD_GRID = 'tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-3 tw:gap-y-1.5 tw:text-sm';

export type FieldSpec = {
  label: string;
  value: React.ReactNode;
  whitespacePreLine?: boolean;
  /** Label above the value, full width (for lists / long text). */
  stackLabel?: boolean;
};

type TestIdProps = { 'data-testid'?: string };

/** A titled block of read-only fields. */
export const FieldSection: React.FC<
  TestIdProps & {
    title: string;
    icon?: React.ReactNode;
    fields: FieldSpec[];
    className?: string;
  }
> = ({ title, icon, fields, className, 'data-testid': testId }) => (
  <section className={className} data-testid={testId}>
    <DrawerSectionHeader text={title} icon={icon} />
    <div className={FIELD_GRID}>
      {fields.map((field) => (
        <DrawerField
          key={field.label}
          label={field.label}
          value={field.value}
          disableIndent
          whitespacePreLine={field.whitespacePreLine}
          stackLabel={field.stackLabel}
        />
      ))}
    </div>
  </section>
);

/** One row of a repeated collection (a license, a reference, ...). */
export const RecordCard: React.FC<
  TestIdProps & {
    title: React.ReactNode;
    rightElement?: React.ReactNode;
    fields: FieldSpec[];
    muted?: boolean;
  }
> = ({ title, rightElement, fields, muted, 'data-testid': testId }) => (
  <div
    className={clsx(
      'tw:rounded-lg tw:border tw:border-zinc-200 tw:bg-white tw:p-3',
      muted && 'tw:opacity-60'
    )}
    data-testid={testId}
  >
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
      <div className="tw:text-sm tw:font-medium tw:text-zinc-800">{title}</div>
      {rightElement}
    </div>
    <div className={FIELD_GRID}>
      {fields.map((field) => (
        <DrawerField
          key={field.label}
          label={field.label}
          value={field.value}
          disableIndent
          whitespacePreLine={field.whitespacePreLine}
          stackLabel={field.stackLabel}
        />
      ))}
    </div>
  </div>
);

/** A titled list of RecordCards with an empty-state line. */
export const RecordSection: React.FC<
  TestIdProps & {
    title: string;
    icon?: React.ReactNode;
    emptyText: string;
    count: number;
    children?: React.ReactNode;
  }
> = ({ title, icon, emptyText, count, children, 'data-testid': testId }) => (
  <section data-testid={testId}>
    {/* Light brand band so a new collection is obvious while scrolling a long tab;
        same type treatment as DrawerSectionHeader. */}
    <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:bg-brand-green-3 tw:px-2 tw:py-1">
      {icon}
      <span className="tw:text-brand-green-0 tw:italic tw:text-sm">{title}</span>
    </div>
    {count === 0 ? (
      // Same footprint as a RecordCard so an empty collection reads as an entry,
      // not a hole in the two-column layout.
      <div className="tw:flex tw:min-h-32 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-dashed tw:border-zinc-300 tw:bg-white tw:p-3 tw:text-sm tw:text-zinc-500">
        {emptyText}
      </div>
    ) : (
      <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:md:grid-cols-2">{children}</div>
    )}
  </section>
);

/**
 * Codes rendered as-is with the CodeDefinition title on hover — for
 * categories whose titles are too long to print inline (board certifications).
 */
export const TooltippedCodes: React.FC<{
  codes: readonly string[];
  map: Record<string, string> | undefined;
}> = ({ codes, map }) => {
  if (codes.length === 0) return <>{EMPTY_VALUE}</>;
  return (
    <span className="tw:inline-flex tw:flex-wrap tw:gap-x-1">
      {codes.map((code, index) => (
        <span key={code} className="tw:whitespace-nowrap">
          <TooltippedText tooltipContent={map?.[code] || code}>
            <span className="tw:cursor-help tw:underline tw:decoration-dotted tw:decoration-zinc-400 tw:underline-offset-2">
              {code}
            </span>
          </TooltippedText>
          {index < codes.length - 1 && ','}
        </span>
      ))}
    </span>
  );
};

/** Paperclip that says whether a document row has an upload — presence only, never a download. */
export const FilePresenceIcon: React.FC<{ hasFile: boolean }> = ({ hasFile }) => (
  <TooltippedText tooltipContent={hasFile ? 'File on file' : 'No file uploaded'}>
    <i
      className={clsx(
        'fa-solid fa-fw fa-paperclip',
        hasFile ? 'tw:text-brand-green-0' : 'tw:text-zinc-300'
      )}
      aria-label={hasFile ? 'File on file' : 'No file uploaded'}
      data-testid={hasFile ? 'file-present' : 'file-missing'}
    />
  </TooltippedText>
);
